import { Injectable } from '@angular/core';

import * as XLSX from 'xlsx';

import { ExcelService as DspxExcelService } from 'dspx-core';
import { ExcelWorkSheetInfo as DspxExcelWorkSheetInfo } from 'dspx-core/lib/constants/excel.constants';

export type ExcelWorkSheetCellObject = XLSX.CellObject | XLSX.WSKeys | any;
export type ExcelWorkSheetCellTransformationFunction = (rowIndex: number, columnIndex: number, cellObject: ExcelWorkSheetCellObject) => void;
export type ExcelWorkSheetInfo = DspxExcelWorkSheetInfo & {
  workSheetCellTransformationFunction?: ExcelWorkSheetCellTransformationFunction;
};

@Injectable({
  providedIn: 'root'
})
export class ExcelService extends DspxExcelService {

  // NOTE:
  // This method is similar to the one defined in DspxExcelService, but it also supports an optional "cell transformation" function.
  // One application of this "cell transformation" function is to set the data type and format of specific cells.
  writeWorkBookToFile(workBookFileName: string, workSheetInfos: ExcelWorkSheetInfo[]): void {

    if (!workBookFileName) {
      throw new Error('workBookFileName must be provided');
    }
    if (!workSheetInfos || !workSheetInfos.length) {
      throw new Error('workSheetInfos must be provided');
    }

    const workBook = XLSX.utils.book_new();

    for (const workSheetInfo of workSheetInfos) {

      let workSheetData = workSheetInfo.workSheetData;
      if (workSheetData.length === 0) {
        // If the worksheet is completely empty, use an empty cell instead, otherwise the parsing will fail
        workSheetData = [];
      }

      const workSheet = XLSX.utils.json_to_sheet(workSheetData, { skipHeader: true });
      this.applyCellTransformation(workSheet, workSheetInfo.workSheetCellTransformationFunction);
      XLSX.utils.book_append_sheet(workBook, workSheet, workSheetInfo.workSheetName);
    }

    const zippedFileExtensions = ['xlsx', 'xlsm', 'xlsb', 'ods'];
    const compression = zippedFileExtensions.some(extension => workBookFileName.endsWith(`.${extension}`));
    XLSX.writeFile(workBook, workBookFileName, { compression });
  }

  private applyCellTransformation(workSheet: XLSX.WorkSheet, workSheetCellTransformationFunction: ExcelWorkSheetCellTransformationFunction): void {

    if (!workSheetCellTransformationFunction) {
      return;
    }

    const range = XLSX.utils.decode_range(workSheet['!ref']);
    for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
      for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex++) {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
        const cellObject: ExcelWorkSheetCellObject = workSheet[cellAddress];
        workSheetCellTransformationFunction(rowIndex, columnIndex, cellObject);
      }
    }
  }
}
